import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import FAQHero from './components/FAQHero';
import FAQCategoryNav from './components/FAQCategoryNav';
import FAQSection from './components/FAQSection';
import FAQContactBanner from './components/FAQContactBanner';

const faqData = {
  konto: {
    title: 'Konto & Innlogging',
    description: 'Alt du trenger å vite om å opprette, administrere og sikre din NordPass-konto.',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_11d337daa-1772191890801.png",
    imageAlt: 'Person using laptop to manage account settings with Norwegian fjord visible through window',
    faqs: [
    {
      question: 'Hvordan oppretter jeg en NordPass-konto?',
      answer: 'Det er enkelt å komme i gang med NordPass. Besøk <strong>nordpass.no/registrer</strong> og fyll inn ditt navn, e-postadresse og et sikkert passord. Du vil motta en bekreftelseslenke på e-post. Klikk på lenken for å aktivere kontoen din. Hele prosessen tar under 2 minutter.',
      relatedLinks: [{ label: 'Registrer deg', href: '/login' }, { label: 'Abonnementer', href: '/nord-pass-subscription-portal' }]
    },
    {
      question: 'Jeg har glemt passordet mitt — hva gjør jeg?',
      answer: 'Gå til innloggingssiden og klikk på <strong>«Glemt passord?»</strong>. Skriv inn e-postadressen din, så sender vi deg en tilbakestillingslenke. Lenken er gyldig i 24 timer. Sjekk spam-mappen din om du ikke mottar e-posten innen 5 minutter.',
      relatedLinks: [{ label: 'Logg inn', href: '/login' }]
    },
    {
      question: 'Kan jeg ha flere kjøretøy på én konto?',
      answer: 'Ja! NordPass støtter registrering av flere kjøretøy på én konto. Med <strong>NordPass Normal</strong> kan du registrere opptil 3 kjøretøy, og med <strong>NordPass Premium</strong> er det ubegrenset antall kjøretøy. Gå til «Min konto» → «Kjøretøy» for å legge til nye kjøretøy.',
      relatedLinks: [{ label: 'Min konto', href: '/nord-pass-subscription-portal' }]
    },
    {
      question: 'Hvordan endrer jeg e-postadressen min?',
      answer: 'Logg inn og gå til <strong>Kontoinnstillinger</strong>. Under «Personlig informasjon» finner du muligheten til å endre e-postadresse. Du vil motta en bekreftelseslenke til den nye adressen. Merk at du ikke kan bruke en e-postadresse som allerede er registrert i systemet.',
      relatedLinks: []
    },
    {
      question: 'Er det mulig å slette kontoen min?',
      answer: 'Du kan slette kontoen din når som helst. Gå til <strong>Kontoinnstillinger → Slett konto</strong>. Vær oppmerksom på at all data, inkludert kjøretøyhistorikk og betalingsinformasjon, vil bli permanent slettet. Aktive abonnementer vil bli avsluttet ved neste faktureringsdato.',
      relatedLinks: [{ label: 'Kontakt support', href: '/nord-pass-contact-page' }]
    },
    {
      question: 'Hvordan aktiverer jeg to-faktor autentisering?',
      answer: 'To-faktor autentisering (2FA) gir ekstra sikkerhet for kontoen din. Gå til <strong>Kontoinnstillinger → Sikkerhet → To-faktor autentisering</strong>. Vi støtter autentiseringsapper som Google Authenticator og Authy, samt SMS-koder. Vi anbefaler å bruke en autentiseringsapp for best sikkerhet.',
      relatedLinks: []
    },
    {
      question: 'Kan jeg dele kontoen min med familiemedlemmer?',
      answer: 'NordPass tilbyr ikke delte kontoer, men vi anbefaler at hvert familiemedlem oppretter sin egen konto. Med <strong>NordPass Premium</strong> kan du legge til alle familiens kjøretøy under én konto. Vi jobber med en familieplan som lanseres i 2026.',
      relatedLinks: [{ label: 'Se abonnementer', href: '/nord-pass-subscription-portal' }]
    },
    {
      question: 'Hva skjer med dataene mine om NordPass legges ned?',
      answer: 'Vi tar datasikkerhet på alvor. I henhold til norsk personvernlovgivning og GDPR vil du alltid ha rett til å eksportere dine data. Vi vil gi minimum 90 dagers varsel ved eventuelle endringer i tjenesten. Alle data lagres på norske servere og er underlagt norsk lov.',
      relatedLinks: [{ label: 'Personvern', href: '/personvern' }]
    }]

  },
  fakturering: {
    title: 'Fakturering & Betaling',
    description: 'Informasjon om abonnementer, betalingsmetoder, fakturaer og refusjoner.',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_112ac7ae4-1772191890457.png",
    imageAlt: 'Close-up of Norwegian krone coins and payment card on wooden desk',
    faqs: [
    {
      question: 'Hvilke betalingsmetoder aksepterer NordPass?',
      answer: 'Vi aksepterer <strong>Visa, Mastercard, American Express</strong>, samt norske betalingsløsninger som <strong>Vipps</strong> og <strong>BankAxept</strong>. Bedriftskunder kan også betale via faktura med 30 dagers betalingsfrist. Alle transaksjoner er kryptert og sikre.',
      relatedLinks: []
    },
    {
      question: 'Når trekkes abonnementsbetalingen?',
      answer: 'Abonnementet fornyes automatisk på samme dato hver måned. Du mottar en e-postvarsling 3 dager før fornyelse. Betalingen trekkes kl. 00:00 norsk tid på fornyelsesdatoen. Du kan se neste betalingsdato under <strong>Min konto → Fakturering</strong>.',
      relatedLinks: [{ label: 'Min konto', href: '/nord-pass-subscription-portal' }]
    },
    {
      question: 'Kan jeg oppgradere eller nedgradere abonnementet mitt?',
      answer: 'Ja, du kan endre abonnement når som helst. Ved <strong>oppgradering</strong> aktiveres de nye funksjonene umiddelbart, og du betaler kun differansen for gjenværende periode. Ved <strong>nedgradering</strong> trer endringen i kraft ved neste faktureringsdato.',
      relatedLinks: [{ label: 'Se abonnementer', href: '/nord-pass-subscription-portal' }]
    },
    {
      question: 'Tilbyr NordPass refusjon?',
      answer: 'Vi tilbyr full refusjon innen <strong>14 dager</strong> etter kjøp i henhold til norsk angrerettlov. Etter 14 dager gis det ikke refusjon for påbegynte perioder. Kontakt support@nordpass.no med ordrenummeret ditt for å initiere en refusjon.',
      relatedLinks: [{ label: 'Kontakt support', href: '/nord-pass-contact-page' }]
    },
    {
      question: 'Hvordan laster jeg ned fakturaen min?',
      answer: 'Alle fakturaer er tilgjengelige under <strong>Min konto → Fakturering → Fakturahistorikk</strong>. Du kan laste ned fakturaer i PDF-format. Fakturaer sendes også automatisk til din registrerte e-postadresse. Bedriftskunder kan legge til organisasjonsnummer og MVA-nummer for korrekte fakturaer.',
      relatedLinks: [{ label: 'Min konto', href: '/nord-pass-subscription-portal' }]
    },
    {
      question: 'Hva er forskjellen mellom abonnementsplanene?',
      answer: '<strong>NordPass Gratis</strong>: 1 kjøretøy, grunnleggende bomkalkulator, veikart.<br/><br/><strong>NordPass Normal (149 kr/mnd)</strong>: Opptil 3 kjøretøy, avansert bomkalkulator, ruteoptimalisering, prioritert support.<br/><br/><strong>NordPass Premium (299 kr/mnd)</strong>: Ubegrenset kjøretøy, alle funksjoner, API-tilgang, dedikert kundekontakt.',
      relatedLinks: [{ label: 'Se alle planer', href: '/nord-pass-subscription-portal' }]
    },
    {
      question: 'Kan jeg pause abonnementet mitt?',
      answer: 'Vi tilbyr ikke pause-funksjonalitet for abonnementer. Du kan imidlertid kansellere abonnementet og beholde tilgang til premium-funksjoner til slutten av inneværende periode. Du kan reaktivere abonnementet når som helst.',
      relatedLinks: []
    }]

  },
  bomkalkulator: {
    title: 'Bomkalkulator',
    description: 'Spørsmål om bomstasjoner, beregninger, kjøretøyklasser og ruteplanlegging.',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1846fb475-1772191890525.png",
    imageAlt: 'Norwegian toll station on highway with mountains in background during winter',
    faqs: [
    {
      question: 'Hvor nøyaktig er bomkalkulatoren?',
      answer: 'Vår bomkalkulator er basert på <strong>offisielle takster fra Statens vegvesen</strong> og oppdateres automatisk ved takstendringer. Nøyaktigheten er over 98% for standardruter. Merk at midlertidige rabatter, spesielle avtaler og fergepriser kan variere. Vi anbefaler alltid å verifisere mot offisielle kilder for kritiske beregninger.',
      relatedLinks: [{ label: 'Prøv kalkulatoren', href: '/toll-calculator' }]
    },
    {
      question: 'Hvilke kjøretøyklasser støttes?',
      answer: 'Kalkulatoren støtter alle norske kjøretøyklasser: <strong>Klasse 1</strong> (personbil, motorsykkel), <strong>Klasse 2</strong> (varebil, minibuss), <strong>Klasse 3</strong> (lastebil, buss). Elbiler og hydrogenbiler beregnes med gjeldende rabatter. Spesielle kjøretøy som veteranbiler og landbruksmaskiner håndteres manuelt.',
      relatedLinks: [{ label: 'Kjøretøyinnstillinger', href: '/nord-pass-subscription-portal' }]
    },
    {
      question: 'Inkluderer kalkulatoren fergepriser?',
      answer: 'Ja, kalkulatoren inkluderer <strong>fergepriser for riksvegferger</strong> administrert av Statens vegvesen. Lokale ferger og private fergeselskaper er ikke inkludert. Fergeprisene oppdateres halvårlig i takt med offisielle takstendringer.',
      relatedLinks: [{ label: 'Prøv kalkulatoren', href: '/toll-calculator' }]
    },
    {
      question: 'Hvordan beregnes rabatter for elbiler?',
      answer: 'Elbilrabatter beregnes automatisk basert på <strong>gjeldende nasjonale og lokale rabattsatser</strong>. Per 2026 er elbilrabatten 50% på de fleste bomstasjoner. Noen bomringer har egne satser. Registrer kjøretøyet ditt som elbil under «Kjøretøyinnstillinger» for korrekte beregninger.',
      relatedLinks: [{ label: 'Registrer kjøretøy', href: '/nord-pass-subscription-portal' }]
    },
    {
      question: 'Kan jeg beregne ruter med flere stopp?',
      answer: 'Med <strong>NordPass Normal og Premium</strong> kan du legge til opptil 10 mellomstasjoner på en rute. Kalkulatoren optimaliserer automatisk rekkefølgen for å minimere bomkostnader og reisetid. Gratis-planen støtter kun punkt-til-punkt beregninger.',
      relatedLinks: [{ label: 'Prøv kalkulatoren', href: '/toll-calculator' }, { label: 'Oppgrader', href: '/nord-pass-subscription-portal' }]
    },
    {
      question: 'Oppdateres bomtakstene automatisk?',
      answer: 'Ja, alle bomtakster oppdateres automatisk i sanntid. Vi mottar datafeeder direkte fra <strong>AutoPASS</strong> og Statens vegvesen. Takstendringer implementeres vanligvis innen 24 timer etter offisiell kunngjøring. Du kan se sist oppdatert dato i kalkulatoren.',
      relatedLinks: []
    },
    {
      question: 'Hva er AutoPASS og støtter NordPass det?',
      answer: '<strong>AutoPASS</strong> er Norges elektroniske bompengesystem. NordPass integrerer med AutoPASS for å hente sanntidstakster og beregne korrekte kostnader. Vi er ikke et betalingssystem for AutoPASS — for å betale bompenger trenger du en AutoPASS-brikke fra din bank eller et bomselskap.',
      relatedLinks: []
    },
    {
      question: 'Kan jeg eksportere beregningsresultater?',
      answer: 'Premium-brukere kan eksportere beregningsresultater som <strong>PDF, CSV eller Excel</strong>. Dette er nyttig for reiseregninger og bedriftsrapportering. Gå til kalkulatoren, utfør beregningen og klikk «Eksporter» øverst til høyre.',
      relatedLinks: [{ label: 'Prøv kalkulatoren', href: '/toll-calculator' }]
    },
    {
      question: 'Støtter kalkulatoren utenlandske kjøretøy?',
      answer: 'Ja, kalkulatoren støtter kjøretøy registrert i alle <strong>EU/EØS-land</strong>. Velg kjøretøyets registreringsland og klasse. Merk at utenlandske kjøretøy ikke alltid har rett til de samme rabattene som norskregistrerte kjøretøy.',
      relatedLinks: []
    }]

  },
  teknisk: {
    title: 'Teknisk Støtte',
    description: 'Hjelp med tekniske problemer, apper, nettleserkompatibilitet og integrasjoner.',
    imageUrl: "https://images.unsplash.com/photo-1653132491302-fbee23efb785",
    imageAlt: 'Close-up of computer circuit board with blue LED lights representing technical support',
    faqs: [
    {
      question: 'Hvilke nettlesere støtter NordPass?',
      answer: 'NordPass fungerer optimalt i <strong>Chrome 90+, Firefox 88+, Safari 14+, Edge 90+</strong> og Opera 76+. Vi anbefaler å alltid bruke den nyeste versjonen av din nettleser for best ytelse og sikkerhet. Internet Explorer støttes ikke.',
      relatedLinks: []
    },
    {
      question: 'Finnes det en mobilapp for NordPass?',
      answer: 'Vi jobber med native mobilapper for <strong>iOS og Android</strong> som lanseres i Q3 2026. Inntil da er nettsiden fullt responsiv og fungerer utmerket på mobilenheter. Legg til nordpass.no som snarvei på hjemskjermen for en app-lignende opplevelse.',
      relatedLinks: []
    },
    {
      question: 'Kartet laster ikke — hva gjør jeg?',
      answer: 'Prøv følgende steg: <strong>1)</strong> Tøm nettleserens cache (Ctrl+Shift+Del). <strong>2)</strong> Deaktiver nettleserutvidelser midlertidig. <strong>3)</strong> Sjekk at JavaScript er aktivert. <strong>4)</strong> Prøv en annen nettleser. Hvis problemet vedvarer, kontakt support med skjermbilde og nettleserinformasjon.',
      relatedLinks: [{ label: 'Kontakt support', href: '/nord-pass-contact-page' }]
    },
    {
      question: 'Hvordan rapporterer jeg en feil i bomtakstene?',
      answer: 'Vi setter stor pris på feilrapporter! Bruk <strong>«Rapporter feil»-knappen</strong> direkte i kalkulatoren, eller send en e-post til data@nordpass.no med: bomstasjonens navn, korrekt takst, kilde (f.eks. link til offisiell takstliste), og dato for feilen. Vi behandler alle rapporter innen 48 timer.',
      relatedLinks: [{ label: 'Kontakt oss', href: '/nord-pass-contact-page' }]
    },
    {
      question: 'Tilbyr NordPass en API for utviklere?',
      answer: 'Ja! <strong>NordPass API</strong> er tilgjengelig for Premium-brukere og bedriftskunder. APIet gir tilgang til bomtakster, ruteberegninger og kjøretøydata. Dokumentasjon er tilgjengelig på api.nordpass.no. Kontakt oss for enterprise-avtaler med høyere rate limits.',
      relatedLinks: [{ label: 'Kontakt oss', href: '/nord-pass-contact-page' }]
    },
    {
      question: 'Dataene mine synkroniseres ikke mellom enheter — hva gjør jeg?',
      answer: 'Synkronisering skjer automatisk ved innlogging. Sjekk at du er logget inn med samme konto på alle enheter. Prøv å logge ut og inn igjen. Hvis problemet vedvarer, gå til <strong>Kontoinnstillinger → Synkronisering → Tving synkronisering</strong>.',
      relatedLinks: []
    },
    {
      question: 'Hvordan integrerer jeg NordPass med regnskapssystemet mitt?',
      answer: 'NordPass støtter eksport til de fleste norske regnskapssystemer via <strong>CSV og XML-format</strong>. Vi har direkte integrasjoner med Tripletex, Visma og Fiken. For andre systemer, bruk vår generelle CSV-eksport. Kontakt enterprise@nordpass.no for skreddersydde integrasjoner.',
      relatedLinks: [{ label: 'Kontakt oss', href: '/nord-pass-contact-page' }]
    },
    {
      question: 'Er NordPass GDPR-kompatibel?',
      answer: 'Ja, NordPass er fullt ut <strong>GDPR-kompatibel</strong>. Vi lagrer alle data på norske servere, samler kun inn nødvendig informasjon, og gir deg full kontroll over dine data. Du kan til enhver tid be om innsyn, retting eller sletting av dine personopplysninger. Les vår personvernerklæring for detaljer.',
      relatedLinks: [{ label: 'Personvern', href: '/personvern' }]
    }]

  }
};

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredData = useMemo(() => {
    const query = searchQuery?.toLowerCase()?.trim();
    const result = {};

    Object.entries(faqData)?.forEach(([catId, catData]) => {
      if (activeCategory !== 'all' && activeCategory !== catId) return;

      const filteredFaqs = query ?
      catData?.faqs?.filter((faq) =>
      faq?.question?.toLowerCase()?.includes(query) ||
      faq?.answer?.toLowerCase()?.includes(query)
      ) :
      catData?.faqs;

      if (filteredFaqs?.length > 0) {
        result[catId] = { ...catData, faqs: filteredFaqs };
      }
    });

    return result;
  }, [searchQuery, activeCategory]);

  const totalResults = Object.values(filteredData)?.reduce((sum, cat) => sum + cat?.faqs?.length, 0);

  return (
    <div style={{ background: '#050A12', minHeight: '100vh' }}>
      {/* Header nav */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(5,10,18,0.95)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '0 5%', height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <Link to="/" style={{
          fontFamily: 'Syne, sans-serif', fontSize: '1.25rem', fontWeight: 800,
          color: '#EEF0F2', textDecoration: 'none', letterSpacing: '-0.04em'
        }}>NordPass</Link>
        <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
          {[
          { label: 'Hjem', path: '/' },
          { label: 'Tjenester', path: '/tjenester-services' },
          { label: 'Kalkulator', path: '/toll-calculator' },
          { label: 'Kontakt', path: '/nord-pass-contact-page' }]?.
          map((item, i) =>
          <Link key={i} to={item?.path} style={{
            fontFamily: 'Inter, sans-serif', fontSize: '0.875rem',
            color: 'rgba(138,149,163,0.7)', textDecoration: 'none',
            transition: 'color 150ms ease'
          }}
          onMouseEnter={(e) => {e.currentTarget.style.color = '#EEF0F2';}}
          onMouseLeave={(e) => {e.currentTarget.style.color = 'rgba(138,149,163,0.7)';}}>
            {item?.label}</Link>
          )}
          <Link to="/login" style={{
            fontFamily: 'Syne, sans-serif', fontSize: '0.8125rem', fontWeight: 700,
            color: '#EEF0F2', textDecoration: 'none', letterSpacing: '0.05em',
            textTransform: 'uppercase', padding: '8px 20px',
            background: '#E8521A', transition: 'background 200ms ease'
          }}
          onMouseEnter={(e) => {e.currentTarget.style.background = '#d4461a';}}
          onMouseLeave={(e) => {e.currentTarget.style.background = '#E8521A';}}>
            LOGG INN</Link>
        </div>
      </nav>
      {/* Hero */}
      <div style={{ paddingTop: '64px' }}>
        <FAQHero searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      </div>
      {/* Category nav */}
      <FAQCategoryNav activeCategory={activeCategory} onCategoryChange={setActiveCategory} />
      {/* Main content */}
      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '80px 5%' }}>
        {/* Results info */}
        {(searchQuery || activeCategory !== 'all') &&
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          marginBottom: '48px', padding: '16px 24px',
          background: 'rgba(59,111,212,0.06)',
          border: '1px solid rgba(59,111,212,0.15)'
        }}>
            <span style={{
            fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem',
            color: '#3B6FD4', letterSpacing: '0.1em', textTransform: 'uppercase'
          }}>RESULTATER</span>
            <span style={{
            fontFamily: 'Syne, sans-serif', fontSize: '1.125rem',
            fontWeight: 700, color: '#EEF0F2'
          }}>{totalResults} spørsmål funnet</span>
            {searchQuery &&
          <span style={{
            fontFamily: 'Inter, sans-serif', fontSize: '0.875rem',
            color: 'rgba(138,149,163,0.6)'
          }}>for «{searchQuery}»</span>
          }
            <button
            onClick={() => {setSearchQuery('');setActiveCategory('all');}}
            style={{
              marginLeft: 'auto', background: 'none', border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(138,149,163,0.6)', fontFamily: 'Inter, sans-serif',
              fontSize: '0.8125rem', padding: '6px 14px', cursor: 'pointer',
              transition: 'all 150ms ease'
            }}
            onMouseEnter={(e) => {e.currentTarget.style.color = '#EEF0F2';}}
            onMouseLeave={(e) => {e.currentTarget.style.color = 'rgba(138,149,163,0.6)';}}>
            Nullstill filter</button>
          </div>
        }

        {/* No results */}
        {totalResults === 0 &&
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{
            fontFamily: 'Syne, sans-serif', fontSize: '4rem',
            fontWeight: 800, color: 'rgba(59,111,212,0.2)',
            letterSpacing: '-0.05em', marginBottom: '16px'
          }}>404</div>
            <div style={{
            fontFamily: 'Syne, sans-serif', fontSize: '1.25rem',
            fontWeight: 700, color: '#EEF0F2', marginBottom: '8px'
          }}>Ingen resultater funnet</div>
            <p style={{
            fontFamily: 'Inter, sans-serif', fontSize: '0.9375rem',
            color: 'rgba(138,149,163,0.6)', marginBottom: '32px'
          }}>Prøv et annet søkeord eller kontakt oss direkte.</p>
            <button
            onClick={() => {setSearchQuery('');setActiveCategory('all');}}
            style={{
              padding: '12px 28px', background: '#E8521A', border: 'none',
              fontFamily: 'Syne, sans-serif', fontSize: '0.875rem', fontWeight: 700,
              color: '#EEF0F2', cursor: 'pointer', letterSpacing: '0.05em', textTransform: 'uppercase'
            }}>
            VIS ALLE SPØRSMÅL</button>
          </div>
        }

        {/* FAQ sections */}
        {Object.entries(filteredData)?.map(([catId, catData]) =>
        <FAQSection
          key={catId}
          categoryId={catId}
          title={catData?.title}
          description={catData?.description}
          faqs={catData?.faqs}
          imageUrl={catData?.imageUrl}
          imageAlt={catData?.imageAlt} />

        )}
      </main>
      {/* Contact banner */}
      <FAQContactBanner />
      {/* Footer strip */}
      <div style={{
        background: '#050A12', borderTop: '1px solid rgba(255,255,255,0.04)',
        padding: '32px 5%', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px'
      }}>
        <div style={{
          fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6875rem',
          color: 'rgba(138,149,163,0.4)', letterSpacing: '0.06em'
        }}>© 2026 NordPass AS — Alle rettigheter forbeholdt</div>
        <div style={{ display: 'flex', gap: '24px' }}>
          {[
          { label: 'Hjem', path: '/' },
          { label: 'Kontakt', path: '/nord-pass-contact-page' },
          { label: 'Personvern', path: '/personvern' }]?.
          map((item, i) =>
          <Link key={i} to={item?.path} style={{
            fontFamily: 'Inter, sans-serif', fontSize: '0.75rem',
            color: 'rgba(138,149,163,0.4)', textDecoration: 'none',
            transition: 'color 150ms ease'
          }}
          onMouseEnter={(e) => {e.currentTarget.style.color = '#8A95A3';}}
          onMouseLeave={(e) => {e.currentTarget.style.color = 'rgba(138,149,163,0.4)';}}>
            {item?.label}</Link>
          )}
        </div>
      </div>
    </div>);

};

export default FAQ;